import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './PermitForm.css';
import { API_BASE } from '../config/apiConfig';

const BuildingPermitForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState({
    permitApplication: null,
    contractorsList: null,
    stateDesignRelease: null,
    fullSetOfPlans: [], // Array for multiple files
    sitePlan: null
  });
  const [formData, setFormData] = useState({
    permitType: 'building',
    // Applicant/Contact Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    contactStreet: '',
    contactCity: '',
    contactState: 'IN',
    contactZip: '',
    
    // Project Location
    streetAddress: '',
    addressLine2: '',
    city: 'West Lafayette',
    state: 'IN',
    zipCode: '47906',
    businessName: '',
    lotNumber: '',
    subdivision: '',
    parcelNumber: '',
    townshipCounty: '',
    
    // Owner Information
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    ownerStreet: '',
    ownerCity: '',
    ownerState: 'IN',
    ownerZip: '',
    
    // Contractor/Contact Information
    contractorName: '',
    contractorPhone: '',
    contractorEmail: '',
    contractorStreet: '',
    contractorCity: '',
    contractorState: 'IN',
    contractorZip: '',
    contractorLicense: '',
    
    // Type of Primary Use (Section A)
    primaryUseType: '',
    multiFamilyUnits: '',
    primaryUseOther: '',
    
    // Type of Construction (Section B)
    constructionType: '',
    electricalUpgradeFrom: '',
    electricalUpgradeTo: '',
    demolitionUnits: '',
    constructionTypeOther: '',
    
    // Permit Details
    description: '',
    squareFootage: '',
    projectValue: '',
    
    // Building Setbacks (Section E)
    setbackFront: '',
    setbackSide1: '',
    setbackSide2: '',
    setbackRear: '',
    
    // Special Designations
    historicalDistrict: '',
    specialFloodHazardArea: '',
    
    // Energy Code (Section I)
    energyCodePerformance: '',
    energyCodePrescriptive: '',
    energyCodeUA: '',
    
    // Fire Safety Notification
    fireSafetyStreetAddress: '',
    fireSafetyTownshipCounty: '',
    fireSafetyComponentTypes: '',
    fireSafetyLocation: '',
    
    notes: ''
  });

  // Auto-save form data to localStorage
  useEffect(() => {
    const saveDraft = () => {
      const draftData = {
        formData,
        attachments: {
          permitApplication: attachments.permitApplication ? { name: attachments.permitApplication.name } : null,
          contractorsList: attachments.contractorsList ? { name: attachments.contractorsList.name } : null,
          stateDesignRelease: attachments.stateDesignRelease ? { name: attachments.stateDesignRelease.name } : null,
          fullSetOfPlans: Array.isArray(attachments.fullSetOfPlans) && attachments.fullSetOfPlans.length > 0 
            ? attachments.fullSetOfPlans.map(f => ({ name: f.name })) 
            : [],
          sitePlan: attachments.sitePlan ? { name: attachments.sitePlan.name } : null
        },
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('buildingPermitDraft', JSON.stringify(draftData));
    };

    // Debounce save to avoid too frequent writes
    const timeoutId = setTimeout(saveDraft, 500);
    return () => clearTimeout(timeoutId);
  }, [formData, attachments]);

  useEffect(() => {
    // Load draft data on mount (if not coming from clone action)
    if (!location.state?.prefillData) {
      const savedDraft = localStorage.getItem('buildingPermitDraft');
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          // Check if draft is recent (within 7 days)
          const draftDate = new Date(draft.timestamp);
          const daysSinceDraft = (new Date() - draftDate) / (1000 * 60 * 60 * 24);
          
          if (daysSinceDraft < 7) {
            setFormData(draft.formData);
            // Note: File objects can't be restored from localStorage, only metadata
            // Silently restore - no notification needed
          } else {
            // Clear old draft
            localStorage.removeItem('buildingPermitDraft');
          }
        } catch (error) {
          console.error('Failed to load draft:', error);
        }
      }
    } else {
      // Pre-fill form if coming from clone action
      setFormData(location.state.prefillData);
      if (location.state.rejectionReason) {
        alert(`Previous application was rejected: ${location.state.rejectionReason}\n\nPlease review and correct your information before resubmitting.`);
      }
      // Clear any existing draft when using prefill data
      localStorage.removeItem('buildingPermitDraft');
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e, fieldName) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      // For fullSetOfPlans, allow multiple files
      if (fieldName === 'fullSetOfPlans') {
        setAttachments(prev => ({
          ...prev,
          [fieldName]: files
        }));
      } else {
        // For other fields, take the first file
        setAttachments(prev => ({
          ...prev,
          [fieldName]: files[0]
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const multipartData = new FormData();

      // Enrich with jurisdiction info based on selected location
      const selectedLocationRaw = localStorage.getItem('selectedCounty');
      let submissionFields = { ...formData };
      if (selectedLocationRaw) {
        try {
          const loc = JSON.parse(selectedLocationRaw);
          submissionFields = {
            ...submissionFields,
            jurisdictionCity: loc.city || '',
            jurisdictionCounty: loc.county || '',
            jurisdictionState: loc.state || '',
            jurisdictionStateAbbr: loc.stateAbbr || ''
          };
        } catch (err) {
          console.error('Failed to parse selectedCounty for building permit:', err);
        }
      }

      // Append text fields
      Object.entries(submissionFields).forEach(([key, value]) => {
        multipartData.append(key, value ?? '');
      });

      // Append specific document files
      Object.entries(attachments).forEach(([key, fileOrFiles]) => {
        if (fileOrFiles) {
          if (Array.isArray(fileOrFiles)) {
            // Handle multiple files (e.g., fullSetOfPlans)
            fileOrFiles.forEach(file => {
              multipartData.append(key, file);
            });
          } else {
            // Handle single file
            multipartData.append(key, fileOrFiles);
          }
        }
      });

      const response = await axios.post(`${API_BASE}/submit`, multipartData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        // Clear draft data on successful submission
        localStorage.removeItem('buildingPermitDraft');
        navigate(`/status/${response.data.submissionId}`);
      }
    } catch (error) {
      alert('Failed to submit application: ' + (error.response?.data?.error || error.message));
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>Building Permit Application</h2>
        {location.state?.originalSubmissionId && (
          <div className="prefill-notice">
            <strong>ℹ️ Pre-filled from previous application</strong>
            <p>Original Submission ID: {location.state.originalSubmissionId}</p>
            <p>Please review and correct the information before submitting.</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          {/* PROJECT LOCATION */}
          <div className="form-section">
            <h3>PROJECT LOCATION</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Address of Construction Site *</label>
                <input
                  type="text"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Address Line 2</label>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>ZIP Code *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Name of Business</label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Lot Number</label>
                <input
                  type="text"
                  name="lotNumber"
                  value={formData.lotNumber}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Subdivision</label>
                <input
                  type="text"
                  name="subdivision"
                  value={formData.subdivision}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Parcel Number</label>
                <input
                  type="text"
                  name="parcelNumber"
                  value={formData.parcelNumber}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* OWNER */}
          <div className="form-section">
            <h3>OWNER</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Owner Name *</label>
                <input
                  type="text"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Owner Phone *</label>
                <input
                  type="tel"
                  name="ownerPhone"
                  value={formData.ownerPhone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Owner Email *</label>
                <input
                  type="email"
                  name="ownerEmail"
                  value={formData.ownerEmail}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Owner Street Address *</label>
                <input
                  type="text"
                  name="ownerStreet"
                  value={formData.ownerStreet}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Owner City *</label>
                <input
                  type="text"
                  name="ownerCity"
                  value={formData.ownerCity}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Owner State *</label>
                <input
                  type="text"
                  name="ownerState"
                  value={formData.ownerState}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Owner ZIP *</label>
                <input
                  type="text"
                  name="ownerZip"
                  value={formData.ownerZip}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* CONTACT/CONTRACTOR */}
          <div className="form-section">
            <h3>CONTACT/CONTRACTOR</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Contact/Contractor Name *</label>
                <input
                  type="text"
                  name="contractorName"
                  value={formData.contractorName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contact/Contractor Phone *</label>
                <input
                  type="tel"
                  name="contractorPhone"
                  value={formData.contractorPhone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contact/Contractor Email *</label>
                <input
                  type="email"
                  name="contractorEmail"
                  value={formData.contractorEmail}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Contact/Contractor Street Address *</label>
                <input
                  type="text"
                  name="contractorStreet"
                  value={formData.contractorStreet}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contact/Contractor City *</label>
                <input
                  type="text"
                  name="contractorCity"
                  value={formData.contractorCity}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contact/Contractor State *</label>
                <input
                  type="text"
                  name="contractorState"
                  value={formData.contractorState}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contact/Contractor ZIP *</label>
                <input
                  type="text"
                  name="contractorZip"
                  value={formData.contractorZip}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Contractor License #</label>
                <input
                  type="text"
                  name="contractorLicense"
                  value={formData.contractorLicense}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* A. Type of Primary Use */}
          <div className="form-section">
            <h3>A. Type of Primary Use</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Select Primary Use Type *</label>
                <select
                  name="primaryUseType"
                  value={formData.primaryUseType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="single_family">Single Family Residence</option>
                  <option value="multi_family">Multi-Family</option>
                  <option value="commercial">Commercial</option>
                  <option value="fraternity_sorority">Fraternity/Sorority</option>
                  <option value="rental">Rental</option>
                  <option value="other">Other (specify if PDMX)</option>
                </select>
              </div>
              {formData.primaryUseType === 'multi_family' && (
                <div className="form-group">
                  <label>Number of Units *</label>
                  <input
                    type="number"
                    name="multiFamilyUnits"
                    value={formData.multiFamilyUnits}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
              )}
              {formData.primaryUseType === 'other' && (
                <div className="form-group full-width">
                  <label>Specify (if PDMX) *</label>
                  <input
                    type="text"
                    name="primaryUseOther"
                    value={formData.primaryUseOther}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          </div>

          {/* B. Type of Construction */}
          <div className="form-section">
            <h3>B. Type of Construction</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Select Construction Type *</label>
                <select
                  name="constructionType"
                  value={formData.constructionType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="new_structure">New Structure</option>
                  <option value="addition_remodel_repair">Addition, Remodel or Repair of Existing Structure</option>
                  <option value="electrical_upgrade">Electrical Upgrade</option>
                  <option value="demolition_only">Demolition Only</option>
                  <option value="change_of_use">Change of Use</option>
                  <option value="build_out">Build-Out</option>
                  <option value="solar">Solar</option>
                  <option value="business_compliance">Business Compliance</option>
                  <option value="other">Other (specify)</option>
                </select>
              </div>
              {formData.constructionType === 'electrical_upgrade' && (
                <>
                  <div className="form-group">
                    <label>From (Amps) *</label>
                    <input
                      type="number"
                      name="electricalUpgradeFrom"
                      value={formData.electricalUpgradeFrom}
                      onChange={handleChange}
                      min="1"
                    />
                  </div>
                  <div className="form-group">
                    <label>To (Amps) *</label>
                    <input
                      type="number"
                      name="electricalUpgradeTo"
                      value={formData.electricalUpgradeTo}
                      onChange={handleChange}
                      min="1"
                    />
                  </div>
                </>
              )}
              {formData.constructionType === 'demolition_only' && (
                <div className="form-group">
                  <label>Number of Units *</label>
                  <input
                    type="number"
                    name="demolitionUnits"
                    value={formData.demolitionUnits}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
              )}
              {formData.constructionType === 'other' && (
                <div className="form-group full-width">
                  <label>Specify *</label>
                  <input
                    type="text"
                    name="constructionTypeOther"
                    value={formData.constructionTypeOther}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          </div>

          {/* C. Description of Work */}
          <div className="form-section">
            <h3>C. Description of Work</h3>
            <div className="form-group full-width">
              <label>Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                required
              />
            </div>
          </div>

          {/* D. Square Footage */}
          <div className="form-section">
            <h3>D. Square Footage of Project</h3>
            <div className="form-group">
              <label>Square Footage</label>
              <input
                type="number"
                name="squareFootage"
                value={formData.squareFootage}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>

          {/* E. Proposed Building Setbacks */}
          <div className="form-section">
            <h3>E. Proposed Building Setbacks</h3>
            <p className="form-note">*SITE PLAN MUST BE INCLUDED WITH APPLICATION*</p>
            <div className="form-grid">
              <div className="form-group">
                <label>Front</label>
                <input
                  type="text"
                  name="setbackFront"
                  value={formData.setbackFront}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Side 1</label>
                <input
                  type="text"
                  name="setbackSide1"
                  value={formData.setbackSide1}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Side 2</label>
                <input
                  type="text"
                  name="setbackSide2"
                  value={formData.setbackSide2}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Rear</label>
                <input
                  type="text"
                  name="setbackRear"
                  value={formData.setbackRear}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Required Documents */}
          <div className="form-section">
            <h3>Required Documents</h3>
            <p className="form-note">Please submit the following documents. Items marked "(If Applicable)" may be skipped if not applicable to your project.</p>
            
            <div className="form-grid">
              <div className="form-group full-width">
                <label>1. Permit Application (ILP Combined Application) *</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'permitApplication')}
                />
                {attachments.permitApplication && (
                  <p className="file-selected">Selected: {attachments.permitApplication.name}</p>
                )}
              </div>

              <div className="form-group full-width">
                <label>2. Contractors List (If Applicable)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'contractorsList')}
                />
                {attachments.contractorsList && (
                  <p className="file-selected">Selected: {attachments.contractorsList.name}</p>
                )}
              </div>

              <div className="form-group full-width">
                <label>3. State Design Release (If Applicable)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'stateDesignRelease')}
                />
                {attachments.stateDesignRelease && (
                  <p className="file-selected">Selected: {attachments.stateDesignRelease.name}</p>
                )}
              </div>

              <div className="form-group full-width">
                <label>4. Full Set of Plans (If Applicable)</label>
                <input
                  type="file"
                  accept=".pdf,.dwg,.dxf"
                  multiple
                  onChange={(e) => handleFileChange(e, 'fullSetOfPlans')}
                />
                {attachments.fullSetOfPlans && 
                 ((Array.isArray(attachments.fullSetOfPlans) && attachments.fullSetOfPlans.length > 0) ||
                  (!Array.isArray(attachments.fullSetOfPlans) && attachments.fullSetOfPlans)) && (
                  <p className="file-selected">
                    Selected: {Array.isArray(attachments.fullSetOfPlans) 
                      ? `${attachments.fullSetOfPlans.length} file(s) selected`
                      : attachments.fullSetOfPlans.name}
                  </p>
                )}
              </div>

              <div className="form-group full-width">
                <label>5. Site Plan (If Applicable)</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
                  onChange={(e) => handleFileChange(e, 'sitePlan')}
                />
                {attachments.sitePlan && (
                  <p className="file-selected">Selected: {attachments.sitePlan.name}</p>
                )}
              </div>
            </div>
          </div>

          {/* Review Areas Info */}
          <div className="form-section info-box">
            <h3>What Will Be Reviewed</h3>
            <p>Your building permit application will be reviewed for:</p>
            <ul className="review-areas-list">
              <li>Zoning Compliance</li>
              <li>Building Codes</li>
              <li>Green-Space Requirements</li>
              <li>Stormwater Drainage</li>
              <li>Contractor Bonding</li>
              <li>Occupancy</li>
            </ul>
          </div>

          {/* F. Estimated Cost */}
          <div className="form-section">
            <h3>F. Estimated Cost of Construction</h3>
            <div className="form-group">
              <label>Estimated Cost ($) *</label>
              <input
                type="number"
                name="projectValue"
                value={formData.projectValue}
                onChange={handleChange}
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>

          {/* G. Historical District */}
          <div className="form-section">
            <h3>G. Historical District</h3>
            <div className="form-group">
              <label>Is this property in a Historical District? *</label>
              <select
                name="historicalDistrict"
                value={formData.historicalDistrict}
                onChange={handleChange}
                required
              >
                <option value="">Select...</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          {/* H. Special Flood Hazard Area */}
          <div className="form-section">
            <h3>H. Special Flood Hazard Area</h3>
            <div className="form-group">
              <label>Is this property in a Special Flood Hazard Area? *</label>
              <select
                name="specialFloodHazardArea"
                value={formData.specialFloodHazardArea}
                onChange={handleChange}
                required
              >
                <option value="">Select...</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          {/* I. Energy Code */}
          <div className="form-section">
            <h3>I. Energy Code</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Performance</label>
                <input
                  type="text"
                  name="energyCodePerformance"
                  value={formData.energyCodePerformance}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Prescriptive</label>
                <input
                  type="text"
                  name="energyCodePrescriptive"
                  value={formData.energyCodePrescriptive}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>UA</label>
                <input
                  type="text"
                  name="energyCodeUA"
                  value={formData.energyCodeUA}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* Fire Safety Notification */}
          <div className="form-section">
            <h3>Fire Safety Notification</h3>
            <p className="form-note">Required for Class 1 or 2 Structures with permits issued after 6/30/2018 using Advanced Structural Components</p>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Street Address</label>
                <input
                  type="text"
                  name="fireSafetyStreetAddress"
                  value={formData.fireSafetyStreetAddress}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Township/County</label>
                <input
                  type="text"
                  name="fireSafetyTownshipCounty"
                  value={formData.fireSafetyTownshipCounty}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group full-width">
                <label>Types of Advanced Structural Components Used</label>
                <input
                  type="text"
                  name="fireSafetyComponentTypes"
                  value={formData.fireSafetyComponentTypes}
                  onChange={handleChange}
                  placeholder="e.g., Lightweight I-joist or roof trusses"
                />
              </div>
              <div className="form-group full-width">
                <label>Location in Structure</label>
                <input
                  type="text"
                  name="fireSafetyLocation"
                  value={formData.fireSafetyLocation}
                  onChange={handleChange}
                  placeholder="e.g., roof, floor, etc."
                />
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="form-section">
            <div className="form-group full-width">
              <label>Additional Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
              />
            </div>
          </div>

          {/* Legal Notice */}
          <div className="form-section legal-notice">
            <p><strong>Important Notice:</strong></p>
            <p>Construction must begin within 90 days of the issuance of an Improvement Location Permit. Construction is to be complete within one year of the issuance of an Improvement Location Permit unless a longer time is authorized by the Building Official.</p>
            <p>As owner or representative of the property for which this application is being filed, I hereby certify that the building and/or land will be used only for the Primary Use listed in Section A, in accordance with the Unified Zoning Ordinance of Tippecanoe County. I further agree that this building (or portion thereof) authorized by this Improvement Location Permit will not be occupied until such time as any known code violations are corrected and until such time as a final inspection has been made and a Certificate of Occupancy has been issued.</p>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BuildingPermitForm;

